import React from 'react';
import VideoContainer from './Video.styl';
import VideoJS from './VideoJS';
import {AIProductsResponse} from '@src/Data/Model/AIProducts';
import Theme from '../../bosons/Theme';
import MindMap from '../MindMap/MindMap';
import SummaryText from '../SummaryText/SummaryText';
import AIProducsService from '../../../../Data/DataSource/AIProducts';
import Quiz from '../Quiz/Quiz';
import {handleError, handleEvent} from '../../../Utils/VideoUtils';
import '../Menu/Menu';
import '../Search/Search';
import '../AIProducts/AIProducts';
import '../CustomBar/CustomBar';
import '../Summary/Summary';

export type VideoProps = {
  id: string;
  src: string;
  token: string;
  cdnKey: string;
  primaryColor?: string;
  poster: string;
  ai?: string;
  mediaCode?: string;
  transcription?: string;
  subtitles?: {
    label: string;
    src: string;
    language: string;
  }[];
  config: {
    autoplay: boolean;
    fullScreen: boolean;
    muted: boolean;
    responsive: boolean;
    preload: boolean;
    loop: boolean;
    pip: boolean;
    controls: boolean;
    originalAspectRatio: boolean;
  };
  aspectRatio: string;
  summaryVideo?: string;
  summaryVideoCdnKey?: string;
  authorization?: string;
  width?: number;
  height?: number;
  autoPlay?: boolean;
  controls?: boolean;
  hasSearch?: boolean;
  waterMark?: string | null;
};
// eslint-disable-next-line import/no-anonymous-default-export
export default ({
  id,
  src,
  token,
  cdnKey,
  primaryColor = '',
  poster = '',
  mediaCode,
  transcription = '',
  subtitles = [],
  config = {
    autoplay: false,
    fullScreen: false,
    muted: false,
    responsive: true,
    preload: true,
    loop: false,
    pip: true,
    controls: true,
    originalAspectRatio: false,
  },
  aspectRatio = '16:9',
  ai = '',
  summaryVideo = '',
  summaryVideoCdnKey = '',
  authorization,
  width,
  height,
  hasSearch = false,
  autoPlay = false,
  controls = true,
  waterMark = '',
  ...rest
}: VideoProps) => {
  const playerRef = React.useRef(null);
  const urlParameters = Object.fromEntries(new URLSearchParams(window.location.search));
  const parameters = urlParameters || {};
  const eventParameters = urlParameters.events;
  const userEventParameters = eventParameters ? eventParameters.split(',') : [];
  // const castParameters = parameters ? JSON.parse(parameters) : {};
  const [summary, setSummary] = React.useState<boolean>(Object.keys(parameters).includes('startSummary')
                                                     && parameters.startSummary === '1');
  const [forceAutoPlay, setForceAutoPlay] = React.useState<boolean>(false);
  const [openSummary, setOpenSummary] = React.useState<boolean>(false);
  const [openSummaryText, setOpenSummaryText] = React.useState<boolean>(false);
  const [openMindMap, setOpenMindMap] = React.useState<boolean>(false);
  const [openQuiz, setOpenQuiz] = React.useState<boolean>(false);
  const [aiProducts, setAIProducts] = React.useState<AIProductsResponse | null>(null);

  // const castParameters = {
  //   autoplay: true,
  //   muted: true,
  //   controls: false,
  // } as any;

  const onLoadAI = async () => {
    if (!mediaCode || !authorization) {
      return;
    }

    if (ai === '' || ai === null) {
      return;
    }

    const result = await AIProducsService({
      mediaCode,
      authorization,
      products: (ai || '').split(',').filter((item) => ['MIND_MAP', 'SUMMARY', 'SUMMARY_TEXT', 'QUESTION'].includes(item)),
    });

    if (result.success) {
      setAIProducts(result);
    }
  }

  const currentAspectRatio = config?.originalAspectRatio ? (parameters.aspectRatio || aspectRatio || '16:9').toString().replace(' / ', ':') : '16:9';

  const videoJsOptions = {
    code: mediaCode,
    authorization,
    transcription,
    mediaCode,
    summary,
    token,
    cdnKey,
    summaryVideo,
    summaryVideoCdnKey,
    poster: poster ? poster : Object.keys(parameters).includes('poster') ? parameters.poster : undefined,
    autoplay: forceAutoPlay || (parameters.hasOwnProperty('autoplay') ? parameters.autoplay === "1" : config?.autoplay),
    controls: parameters.hasOwnProperty('controls') ? parameters.controls === "1" : config?.controls,
    controlBar: {
      volumePanel: {
        inline: false,
      },
    },
    loop: parameters.hasOwnProperty('loop') ? parameters.loop === "1" : config?.loop,
    responsive: parameters.hasOwnProperty('responsive') ? parameters.responsive === "1" : config?.responsive,
    fullscreen: parameters.hasOwnProperty('fullscreen') ? parameters.fullscreen === "1" : config?.fullScreen,
    fluid: parameters.hasOwnProperty('fluid') ? parameters.fluid === "1" : true,
    muted: parameters.hasOwnProperty('muted') ? parameters.muted === "1" : config?.muted,
    preload: parameters.hasOwnProperty('preload') ? parameters.preload === "1" : config?.preload,
    aspectRatio: parameters.hasOwnProperty('aspectRatio') ? parameters.aspectRatio : currentAspectRatio,
    fill: true,
    playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
    enableDocumentPictureInPicture: parameters.hasOwnProperty('pip') ? parameters.pip === "1" : config?.pip,
    plugins: {
      hotkeys: {
        volumeStep: parameters.hasOwnProperty('volumeStep') ? parameters.volumeStep : 0.05,
        seekStep: parameters.hasOwnProperty('seekStep') ? parameters.seekStep : 5,
        enableVolumeScroll: false,
        enableModifiersForNumbers: false,
      },
      ...(parameters.hasOwnProperty('watermarkText') || parameters.hasOwnProperty('watermarkImage') ? {
        awesomeWatermark: {
          type: parameters.hasOwnProperty('watermarkText') ? 'text' : 'img',
          intermittent: true,
          hiddenTime: parameters.hasOwnProperty('watermarkTime') ? parseInt(parameters.watermarkTime) : 15,
          visibleTime: parameters.hasOwnProperty('watermarkTime') ? parseInt(parameters.watermarkTime) : 15,
          opacity: parameters.hasOwnProperty('watermarkOpacity') ? parameters.watermarkOpacity : 0.8,
          position: parameters.hasOwnProperty('watermarkPosition') ? parameters.watermarkPosition : 'top-right',
          positionUnit: 'px',
          randomPositionSwitchingTime: 4,
          top: null,
          bottom: null,
          left: null,
          right: null,
          ...(parameters.hasOwnProperty('watermarkText') ? {
            fontColor: Theme.dark.color.grey[12],
            fontFamily: 'Inter',
            fontSize: '60',
            fontSizeUnit: 'px',
            text: parameters.watermarkText,
          } : {}),
          image: (parameters.hasOwnProperty('watermarkImage') ? parameters.watermarkImage : waterMark),
          imageWith: '60px',
          url: parameters.hasOwnProperty('watermarkUrl') ? parameters.watermarkUrl : null,
        },
      } : {}),
      hlsQualitySelector: {displayCurrentQuality: true},
      settingsMenu: {
        items: ['QualityButton', 'PlaybackRateMenuButton', 'AudioTrackButton', 'ChaptersButton'],
        languages: {
          settings: 'Configurações',
          loading: 'Carregando',
          back: 'Voltar',
          subtitles_off: 'Desativado',
          "subtitles off": 'Desativado',
          subtitles: 'Desativado',
          captions_off: 'Desativado',
          "captions off": "Desativado",
          captions: 'Legendas ativadas',
          default_audio: 'Áudio padrão',
          audio: 'Áudio',
          chapters: 'Capítulos',
          speed: 'Velocidade',
          quality: 'Qualidade',
        },
      },
      searchButtonPlugin: {
        enableSearchButton: hasSearch,
        mediaCode,
        authorization,
        onChangeSearch: () => {
          setOpenQuiz(false);
          setOpenSummary(false);
          setOpenMindMap(false);
        }
      },
      summaryPlugin: {
        createSummaryButton: !!summaryVideo,
        enableSummaryButton: !!summaryVideo,
        isSummary: summary,
        setSummary: (isSummary: boolean) => {
          setSummary(isSummary);
          setForceAutoPlay(true);

          handleEvent('summaryChange', playerRef.current as any, {
            summary: isSummary,
          });

          try {
            const tracks = (playerRef.current as any).textTracks();

            for (var i = tracks.length - 1; i >= 0; i--) {
              if (isSummary && transcription) {
                tracks[i].mode = 'hidden';
              } else {
                tracks[i].mode = '';
              }
            }
          } catch (exception) {
            console.log('Problem to control subtitles', exception);
          }
        },
      },
      customBarPlugin: {
        buildCustomBar: true,
      },
      aiProducts: {
        ai,
        onLoadAI,
        changeOpenedMindMap: (opened: boolean) => {
          setOpenMindMap(opened);
          setOpenQuiz(false);
          setOpenSummary(false);
        },
        changeOpenedSummary: (opened: boolean) => {
          setOpenSummary(opened);
          setOpenMindMap(false);
          setOpenQuiz(false);
        },
        changeOpenedQuiz: (opened: boolean) => {
          setOpenQuiz(opened);
          setOpenSummary(false);
          setOpenMindMap(false);
        },
      }
    },
    sources: [
      {
        src: summary ? summaryVideo : src,
        type: 'application/x-mpegURL',
      },
    ],
    tracks: subtitles.map((subtitle) => ({
      src: subtitle.src,
      kind: 'subtitles',
      label: subtitle.label,
      language: subtitle.language,
    })),
    // tracks: !summary && transcription ? [
    //   {
    //     src: transcription,
    //     kind: 'subtitles',
    //     label: 'Transcrição (CC)',
    //     language: 'pt-br',
    //   }
    // ] : [],
    // techOrder: ["chromecast", "html5"],
  };

  const handlePlayerReady = (player: any) => {
    playerRef.current = player;

    //TODO
    player.on('playing', () => {
      console.log('player is playing');
    });

    //TODO
    player.on('waiting', () => {
      console.log('player is waiting');
    });

    //TODO
    player.on('dispose', () => {
      console.log('player will dispose');
    });

    //TODO
    player.on('error', () => {
      console.log('player error', player.error_);

      return handleError(player.error_.code, player.error_.message, player)
    });
    userEventParameters.forEach(eventName => player.on(eventName, () => handleEvent(eventName, player)));

    // Prevent right mouse button click
    const playerElement = player.el();
    playerElement.addEventListener('contextmenu', (eventName: any) => {
      eventName.preventDefault();
    });

    const urlParameters = Object.fromEntries(new URLSearchParams(window.location.search));
    const parameters = urlParameters || {};

    if (Object.keys(parameters).includes('startSummary')
     && parameters.startSummary === '1') {
      setSummary(true);
    }

    if (Object.keys(parameters).includes('seekTo')) {
      player.currentTime(parseInt(parameters.seekTo));
    }

    //TODO
    console.log('Player ready');
    // player.chromecast();
    // player.airPlay();
  };

  const Video = React.useMemo(() => {
    //TODO
    console.log('Re-render video', JSON.stringify(videoJsOptions));

    return (
      <VideoJS options={videoJsOptions} onReady={handlePlayerReady} />
    );
  }, [JSON.stringify(videoJsOptions)]);

  return React.useMemo(() => {
    //TODO
    console.log('Re-render video container', mediaCode);

    return (
      <VideoContainer primaryColor={parameters.primaryColor || primaryColor || ''}>
        {Video}
        <MindMap key="mindMap" mediaCode={mediaCode || ''} authorization={authorization || ''} text={aiProducts?.mindMap || ''} opened={openMindMap} onClose={() => setOpenMindMap(false)} />
        <SummaryText type="summary" key="summary" mediaCode={mediaCode || ''} authorization={authorization || ''} text={aiProducts?.summary || ''} opened={openSummary} onClose={() => setOpenSummary(false)} />
        <SummaryText type="summaryText" key="summaryText" mediaCode={mediaCode || ''} authorization={authorization || ''} text={aiProducts?.summaryText || ''} opened={openSummaryText} onClose={() => setOpenSummaryText(false)} />
        {
          (aiProducts?.questions || []).length > 0 ? (
            <Quiz key="quiz" mediaCode={mediaCode || ''} authorization={authorization || ''} questions={aiProducts?.questions || []} opened={openQuiz} onClose={() => setOpenQuiz(false)} />
          ) : null
        }
      </VideoContainer>
    );
  }, [summary, parameters, videoJsOptions, openSummary, openMindMap, openQuiz]);
};
